/*Global Dependencies*/
import React, { useState, useRef, useEffect } from "react";
import { Input, Space, Table, Button, Pagination, Select ,Empty} from "antd";
import { SearchOutlined, CaretDownOutlined } from "@ant-design/icons";
import { cloneDeep, get, map , filter} from "lodash";
import classnames from "classnames";

/*Local Dependencies*/
import { fixedDecimal } from "@utils/helpers";
import { ASC, DESC } from "@utils/constants";
import DataNotFound from "@components/noDataFound";

const { Option } = Select;
const rowsPerPageArray: any[] = ["5", "10", "15", "20", "25", "30", "35", "40", "45", "50"];

const TablePreloader = ({ rowsPerPage }: { rowsPerPage: number }) => {
  return (
    <div className="table-preloader">
      {map(Array.from(Array(rowsPerPage + 1).keys()), (item) => {
        return (
          <div className="table-preloader__row" key={"row_" + item}>
            {map(Array.from(Array(8).keys()), (item) => {
              return (
                <div key={"col_" + item} className="table-preloader__row__column">
                  <div className="table-preloader__row__column__title shimmerBG"></div>
                  <div className="table-preloader__row__column__sub-title shimmerBG"></div>
                </div>
              );
            })}
          </div>
        );
      })}
      <div className="pagination">
        <div className="rowperpage_preloader shimmerBG"></div>
        <div className="pagination_text_preloader shimmerBG"></div>
      </div>
    </div>
  );
};

const GenericTable = ({
  columns, // the columns array of objects
  tableData, // data for the table in the form of array os objects
  setTableData, // function to set the state of table data
  highlighterDiv, // function to change the rendering of columns 
  columnSearchInfo, // state to store the search data of all the columns 
  setColumnSearchInfo, // function to update the search data of all the columns 
  searchRequired, // boolean value to inidicate if search is required for columns
  rowsPerPage, // number count to indicate number of rows to be displayed in the table
  setRowsPerPage, // state to set rows per page
  currentPage, // the current page of the table
  setCurrentPage, // function to set the current page of the table
  paginationRequired, // boolean value to indicate if pagination is required
  tableLoader, // boolean to display loading state of the table
  totalCount, // total count of the number of rows in the table 
  getTableData, // function to invoke the gettabledata function based on search , sort , and pagination changes 
  handleSortChange, // function to handle sort change
  searchKeywordNew, // state to store the search state
  setSearchKeywordNew, // function to set the search state
  noColSearchSort = [], // array of dataIndex for which the no search and sort is required in the header
  renderCols = [], // array of dataIndex for which the column render should happen like the render value in columns array 
  costCols = [], // dataIndex of the columns which have cost needed to be displayed with a dollar sign before it 
  noDataText = undefined // text to display when no data is present
}: any) => {

  const [tempTableData, setTempTableData] = useState(tableData);
  const [rowsPerPageToggle, setRowsPerPageToggle] = useState<boolean>(false);
  const [emptyMessage , setEmptyMessage] = useState<string | undefined>(noDataText);

  useEffect(() => {
    console.log("genericTable.useEffect")
    setTempTableData(tableData);
  }, [tableData]);

  useEffect(() => {
    setEmptyMessage(noDataText);
  },[noDataText])

  const inputref = useRef<any>(null);
  const tableClassName = classnames({ dnone: tableLoader });
  const tablePreloaderClassName = classnames({ dnone: !tableLoader });
  const tableConfig: Array<{}> = map(cloneDeep(columns), (dataInfo: any) => {
    console.log("genericTable.tableConfig")
    return {
      ...dataInfo,

      ...(searchRequired && {
        filterDropdown: noColSearchSort.includes(dataInfo.dataIndex) ? null : ({ confirm }: any) => (
          <div style={{ padding: "10px" }}>
            <Input
              ref={inputref}
              style={{ marginBottom: 8, display: "block" }}
              value={get(columnSearchInfo, dataInfo.dataIndex, "")}
              onChange={(e) => {
                setColumnSearchInfo({
                  ...columnSearchInfo,
                  [dataInfo.dataIndex]: e.target.value,
                });
              }}
              onPressEnter={(e: any): any => {
                applyColumnSearch(dataInfo.dataIndex, "");
                confirm({ closeDropdown: true });
                e.stopPropagation();
              }}
            />
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
                onClick={(e) => {
                  setColumnSearchInfo({
                    ...columnSearchInfo,
                    [dataInfo.dataIndex]: get(columnSearchInfo, dataInfo.dataIndex, ""),
                  });
                  applyColumnSearch(dataInfo.dataIndex, "");
                  confirm({ closeDropdown: true });
                }}
              >
                Search
              </Button>
              <Button
                onClick={() => {
                  setColumnSearchInfo({...columnSearchInfo, [dataInfo.dataIndex]: "" });
                  applyColumnSearch(dataInfo.dataIndex, "" , true);
                  confirm({ closeDropdown: true });
                }}
                size="small"
                style={{ width: 90 }}
              >
                Reset
              </Button>
            </Space>
          </div>
        ),
        filterIcon:
          noColSearchSort.includes(dataInfo.dataIndex)
            ? null
            : () => (
                <SearchOutlined
                  style={{
                    color: get(columnSearchInfo, dataInfo.dataIndex, "") !== "" ? "#EF6D2A" : undefined,
                  }}
                />
              ),
      }),
      render:
      renderCols.includes(dataInfo.dataIndex) 
          ? dataInfo.render
          : costCols.includes(dataInfo.dataIndex) 
          ? (text: any, record: any) => highlighterDiv("$ " + fixedDecimal(text, 0), get(columnSearchInfo, dataInfo.dataIndex, ""), dataInfo.dataIndex)
          : (text: any, record: any) => highlighterDiv(text, get(columnSearchInfo, dataInfo.dataIndex, ""), dataInfo.dataIndex, record),
      onFilter: (value: any, record: any) => (record[dataInfo.dataIndex] ? record[dataInfo.dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : ""),
      onFilterDropdownOpenChange: (visible: any) => {
        if (visible) {
          setTimeout(() => inputref.current?.select(), 100);
        }
      },
    };
  });

  const applyColumnSearch = (columnName: any, text: any , resetFlag ?: boolean) => {
    console.log("genericTable.applyColumnSearch")
    text = text ? text : get(columnSearchInfo, [columnName], "");
    let sBy: string | undefined = undefined;
    let sKeyword = undefined;
    if (columnName?.trim() !== "" || text.trim() !== "") {
      sBy = columnName;
      sKeyword = text;
    }

    let searchOn = filter(searchKeywordNew, item => {
      return item.columnName !== sBy;
    });
    let x  = resetFlag === true ? searchOn : [...searchOn, { columnName: sBy, searchText: sKeyword }];
    setSearchKeywordNew(x);
    setCurrentPage(1);
    getTableData({searchOn : x , page : 1});
  };

  const changeCurrentPage = (e: number) => {
    console.log("genericTable.changeCurrentPage")
    setCurrentPage(e);
    getTableData({page : e});
  };

  const changeRowsPerPage = (e: number) => {
    console.log("genericTable.changeRowsPerPage")
    setCurrentPage(1);
    setRowsPerPage(e);
    getTableData({ limit: e , page : 1});
  };

  const handleChange = (pagination: any, filters: any, sorter: any, extra: any) => {
    console.log("genericTable.handleChange")
    if (extra.action === "sort") {
      let sortType = sorter?.order === "ascend" ? ASC : sorter?.order === "descend" ? DESC : sorter?.order;
      handleSortChange(sorter.columnKey, sortType);
    }
  };

  let locale = {
    emptyText: (
      <span>
        <p>
        {/* <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyMessage}/> */}
          <DataNotFound message={emptyMessage} />
        </p>
      </span>
    )
  };
  

  return (
    <>
      <div className={tableClassName}>
        <Table
          bordered={true}
          columns={tableConfig}
          dataSource={tempTableData}
          className="table-rows"
          onChange={handleChange}
          locale={locale}
          // pagination={{
          //   total: tempTableData.length,
          //   defaultPageSize: 5,
          //   showSizeChanger: true,
          //   pageSizeOptions: ["5", "10", "15", "20", "25", "30"],
          //   showTotal: (total, range) => {
          //     return <span className="paginationData">{`${range[0]} to ${range[1]} of ${total}`}</span>;
          //   },
          // }}
          pagination={false}
        />
        {(paginationRequired && tempTableData?.length > 0) && (
          <div className="pagination">
            <div>
              Show
              <Select
                defaultValue={rowsPerPage}
                style={{ width: 64, margin: "8px 8px" }}
                onClick={() => setRowsPerPageToggle(!rowsPerPageToggle)}
                onChange={(e) => changeRowsPerPage(e)}
                open={rowsPerPageToggle}
                suffixIcon={<CaretDownOutlined />}
              >
                {rowsPerPageArray.map((val) => (
                  <Option key={val} value={val}>
                    {val}
                  </Option>
                ))}
              </Select>
              rows per page
            </div>
            {tempTableData?.length > 0 && (
              <Pagination
                total={totalCount}
                showTotal={(total: any, range: any) => (
                  <>
                    <span style={{ color: "#091e42" }}>
                      {rowsPerPage * currentPage - (rowsPerPage - 1)} -{" "}
                      {rowsPerPage * currentPage > tempTableData?.length
                        ? tempTableData?.length + rowsPerPage * currentPage - (rowsPerPage - 1) - 1
                        : rowsPerPage * currentPage + rowsPerPage * currentPage - (rowsPerPage - 1) - 1}
                    </span>
                    <span> of {totalCount}</span>
                  </>
                )}
                key={rowsPerPage}
                defaultPageSize={rowsPerPage}
                defaultCurrent={currentPage}
                current={currentPage}
                showSizeChanger={false}
                onChange={(e: any) => {
                  changeCurrentPage(e);
                }}
              />
            )}
          </div>
        )}
      </div>
      <div className={tablePreloaderClassName}>
        <TablePreloader rowsPerPage={5} />
      </div>
    </>
  )
};

export default GenericTable;
